'use client';

import { useEffect } from 'react';
import { useAtom, useAtomValue } from 'jotai';
import { useIsClient } from 'usehooks-ts';
import {
  useActivateVoucher,
  useAutoActivateCampaignCoupon,
  useAutoApplyVoucher,
} from '@/hooks/useCartHooks';
import { useIsPromotionBannerDisabled } from '@/hooks/useIsPromotionBannerDisabled';
import { usePageChrome } from '@/hooks/usePageChrome';
import { useTrackPromo } from '@/hooks/useTrackPromo';
import {
  activePromoCodeAtom,
  isPromoExpiredAtom,
  openPromoPopupAtom,
  promoConfigAtom,
} from '@/atoms/promoAtom';
import { getPromoExpiryDate, isPromoPopupClosed, setPromoPopupClosed } from '@/utility/promos';
import PromoPopup from '../PromoPopup/PromoPopup';
import { PromoAnnouncementBar } from './PromoAnnouncementBar';

/**
 * Gate for the promo banner. The page shell is shared by every route, so this
 * component is always rendered and has to opt out itself — the per-page-type
 * layouts used to do it with `{!hidePromoBanner && ...}`.
 *
 * The check has to happen *before* `PromoBanner`'s hooks, not inside it:
 * `useAutoApplyVoucher` posts the active CMS campaign coupon to the cart on
 * mount, and `useAutoActivateCampaignCoupon` activates an auto-apply campaign's
 * coupon, neither of which must happen on routes that never showed the banner.
 */
export default function HeaderPromoBanner() {
  const { hidePromoBanner } = usePageChrome();
  const isPromotionBannerDisabled = useIsPromotionBannerDisabled();

  if (hidePromoBanner || isPromotionBannerDisabled) {
    return null;
  }

  return <PromoBanner />;
}

function PromoBanner() {
  const { pageType } = usePageChrome();
  const [showPopup, setShowPopup] = useAtom(openPromoPopupAtom);
  const promoSlot = useAtomValue(promoConfigAtom);
  const isExpired = useAtomValue<boolean>(isPromoExpiredAtom);

  const { applyVoucher } = useActivateVoucher();
  useAutoActivateCampaignCoupon();
  useAutoApplyVoucher();
  const activePromoCode = useAtomValue(activePromoCodeAtom);

  const trackPopup = useTrackPromo();
  const isClient = useIsClient();

  const voucherCode = promoSlot?.content['promo-tool'].campaignDetails.code;
  const isPromoActivated = activePromoCode?.code === voucherCode;

  useEffect(() => {
    if (pageType === 'DESIGNER' || isExpired) return;

    // A URL coupon wins for the session: don't nag with the CMS promo popup.
    if (activePromoCode?.fromUrl) return;

    // Dont show if user already closed the popup for this promo code
    if (!voucherCode || isPromoPopupClosed(voucherCode)) return;

    // Dont show if promo code is already activated
    if (isPromoActivated) return;

    const timer = setInterval(() => {
      if (window.sprdConsent && window.sprdConsent.getStatus()?.necessary === true) {
        setShowPopup(true);
        trackPopup('pop up');
        clearInterval(timer);
      }
    }, 3000);

    return () => clearInterval(timer);
  }, [
    pageType,
    isExpired,
    isPromoActivated,
    voucherCode,
    trackPopup,
    setShowPopup,
    activePromoCode,
  ]);

  if (!promoSlot || !isClient || isExpired) {
    return null;
  }

  const promoToolModuleData = promoSlot.content['promo-tool'];

  const handleClose = () => {
    setShowPopup(false);
    if (voucherCode) {
      setPromoPopupClosed(voucherCode, getPromoExpiryDate());
    }
  };

  const handleUserOpen = () => {
    setShowPopup(true);
    trackPopup('header banner');
  };

  return (
    <>
      <PromoAnnouncementBar
        content={promoToolModuleData.announcementBar}
        onCtaClick={handleUserOpen}
        expiryDate={promoSlot.content._meta.lifecycle?.expiryTime}
      />
      <PromoPopup
        open={showPopup}
        onClose={handleClose}
        applyVoucher={applyVoucher}
        activePromoCode={activePromoCode?.code}
      />
    </>
  );
}
